// ===== BEGIN FILE: src/components/HouseDetailsSidePanel.tsx =====
/**
 * House Details Side Panel
 * Shows detailed information about a selected house including:
 * - House number, sign chip + element chip, house signification
 * - Planet Impact score (renamed from House Score)
 * - House lord with "Lord X in Hy (Nth from own)" italic description
 */

import React from 'react';
import { Box, Typography, Chip, Sheet } from '@mui/joy';
import { RASHI_NAMES } from '../utils/astroEngine';
import type { HouseInterpretation } from '../utils/fetchHouseInterpretations';
import { computeHouseCombinedScore, getHouseRating } from '../utils/houseTemporalScores';

type PlanetName = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';

interface HouseDetailsSidePanelProps {
  houseNumber: number; // 1-12
  signNumber: number; // 1-12 (Rashi)
  houseScore?: number; // Total house influence score (planet impact, -50..+50)
  lordName: PlanetName; // Lord of the sign
  lordHouse: number; // House where lord is sitting (1-12)
  houseInterpretations?: HouseInterpretation[]; // Lordship text from Supabase
  bhavaBalaRupas?: Record<number, number>; // Bhava Bala (0-100) per house
}

const SIGN_LORD: Record<number, PlanetName> = {
  1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
  7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
};

// House significations — matches HouseScoresCompactView HOUSE_LABELS
const HOUSE_LABELS: Record<number, { primary: string; keywords: string }> = {
  1:  { primary: 'Persona',       keywords: 'Self, Body, Vitality' },
  2:  { primary: 'Wealth',        keywords: 'Family, Speech, Assets' },
  3:  { primary: 'Communication', keywords: 'Siblings, Courage, Skills' },
  4:  { primary: 'Property',      keywords: 'Home, Mother, Peace' },
  5:  { primary: 'Children',      keywords: 'Creativity, Intelligence' },
  6:  { primary: 'Health',        keywords: 'Service, Obstacles, Enemies' },
  7:  { primary: 'Spouse',        keywords: 'Partnership, Marriage' },
  8:  { primary: 'Longevity',     keywords: 'Transformation, Occult' },
  9:  { primary: 'Fortune',       keywords: 'Father, Luck, Dharma' },
  10: { primary: 'Career',        keywords: 'Status, Reputation, Authority' },
  11: { primary: 'Gains',         keywords: 'Friends, Aspirations, Income' },
  12: { primary: 'Expenses',      keywords: 'Loss, Liberation, Foreign' },
};

// Sign names (1-indexed)
const SIGN_NAMES: Record<number, string> = {
  1: 'Aries', 2: 'Taurus', 3: 'Gemini', 4: 'Cancer',
  5: 'Leo', 6: 'Virgo', 7: 'Libra', 8: 'Scorpio',
  9: 'Sagittarius', 10: 'Capricorn', 11: 'Aquarius', 12: 'Pisces',
};

const SIGN_SYMBOLS: Record<number, string> = {
  1: '♈', 2: '♉', 3: '♊', 4: '♋',
  5: '♌', 6: '♍', 7: '♎', 8: '♏',
  9: '♐', 10: '♑', 11: '♒', 12: '♓',
};

// Fire, Earth, Air, Water elements — matches HouseScoresCompactView
const SIGN_ELEMENT: Record<number, { name: string; color: string; bg: string }> = {
  1:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  2:  { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  3:  { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  4:  { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
  5:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  6:  { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  7:  { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  8:  { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
  9:  { name: 'Fire',  color: '#dc2626', bg: '#fef2f2' },
  10: { name: 'Earth', color: '#65a30d', bg: '#f7fee7' },
  11: { name: 'Air',   color: '#0891b2', bg: '#ecfeff' },
  12: { name: 'Water', color: '#2563eb', bg: '#eff6ff' },
};

const PLANET_ABBR: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

function ordinal(n: number): string {
  if (n === 0) return 'own';
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

export default function HouseDetailsSidePanel({
  houseNumber,
  signNumber,
  houseScore,
  lordName,
  lordHouse,
  houseInterpretations,
  bhavaBalaRupas,
}: HouseDetailsSidePanelProps) {
  const signName  = SIGN_NAMES[signNumber]  || RASHI_NAMES[signNumber - 1] || `Sign ${signNumber}`;
  const signSymbol = SIGN_SYMBOLS[signNumber] || '';
  const element   = SIGN_ELEMENT[signNumber];
  const label     = HOUSE_LABELS[houseNumber];

  // Lord position from house (Vedic inclusive counting, 0 = own house)
  const offset = (lordHouse - houseNumber + 12) % 12;
  const lordSignNumber = ((signNumber - 1 + offset) % 12) + 1;
  const lordSignName   = SIGN_NAMES[lordSignNumber] || RASHI_NAMES[lordSignNumber - 1] || `Sign ${lordSignNumber}`;
  const lordAbbr       = PLANET_ABBR[lordName] || lordName;
  // e.g. "Lord Me in H5 (5th from own) · Virgo"  — offset+1 for Vedic inclusive count
  const lordDescription = offset === 0
    ? `Lord ${lordAbbr} in H${lordHouse} (own house) · ${lordSignName}`
    : `Lord ${lordAbbr} in H${lordHouse} (${ordinal(offset + 1)} from own) · ${lordSignName}`;

  // Lordship interpretation text from Supabase (same source as compact dashboard tooltip)
  const hInterp = houseInterpretations?.find(hi => hi.house === houseNumber);
  const lordInterp = hInterp?.lordship?.[0]?.interpretation ?? hInterp?.lordship?.[0]?.overview;

  // Combined score — same formula as the donut ring in HouseScoresCompactView
  const bhavaBala = bhavaBalaRupas?.[houseNumber] ?? null;
  const combinedScore = houseScore !== undefined
    ? computeHouseCombinedScore(bhavaBala, houseScore, 50, 50)
    : null;
  const rating = combinedScore !== null ? getHouseRating(combinedScore) : null;
  const ratingColor: Record<string, string> = {
    Excellent: '#16a34a', Good: '#2563eb', Fair: '#d97706', 'Needs Attention': '#dc2626',
  };

  const formattedScore = houseScore !== undefined
    ? (houseScore >= 0 ? `+${houseScore.toFixed(1)}` : houseScore.toFixed(1))
    : 'N/A';

  const scoreColor = houseScore !== undefined
    ? (houseScore >= 30 ? '#16a34a' : houseScore >= 10 ? '#84cc16' : houseScore >= -10 ? '#d4b106' : houseScore >= -30 ? '#f97316' : '#dc2626')
    : '#94a3b8';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 280,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        boxSizing: 'border-box',
      }}
    >
      {/* ── Header: House chip · Sign chip · Element chip · Combined score chip ── */}
      <Box data-audio-cue="chart-house-header" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
        <Chip size="sm" variant="solid" color="primary"
          sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.25, px: 1 }}>
          H{houseNumber}
        </Chip>
        <Chip size="sm" variant="soft" color="neutral"
          sx={{ fontWeight: 600, fontSize: '0.68rem' }}>
          {signSymbol} {signName}
        </Chip>
        {element && (
          <Chip size="sm" variant="soft"
            sx={{ fontWeight: 600, fontSize: '0.65rem', color: element.color, bgcolor: element.bg, border: 'none' }}>
            {element.name}
          </Chip>
        )}
        {combinedScore !== null && rating && (
          <Chip size="sm" variant="soft"
            sx={{ fontWeight: 700, fontSize: '0.65rem', color: ratingColor[rating], bgcolor: `${ratingColor[rating]}18`, border: 'none' }}>
            {combinedScore}% · {rating}
          </Chip>
        )}
      </Box>

      {/* ── House signification ── */}
      {label && (
        <Box data-audio-cue="chart-house-signification" sx={{ mb: 0.25 }}>
          <Typography level="title-sm" sx={{ fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.2 }}>
            {label.primary}
          </Typography>
          <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.3 }}>
            {label.keywords}
          </Typography>
        </Box>
      )}

      {/* ── Planet Impact ── */}
      <Sheet data-audio-cue="chart-planet-impact" variant="soft" sx={{ p: 0.75, borderRadius: 'sm' }}>
        <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25, color: 'text.secondary', fontSize: '0.65rem' }}>
          PLANET IMPACT
        </Typography>
        <Typography level="body-sm"
          sx={{ fontWeight: 700, color: scoreColor, fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {formattedScore}
        </Typography>
        <Typography level="body-xs" sx={{ fontSize: '0.6rem', color: 'text.tertiary', mt: 0.25 }}>
          Blended Planetary Influence
        </Typography>
      </Sheet>

      {/* ── House Lord ── */}
      <Sheet data-audio-cue="chart-house-lord" variant="soft" sx={{ p: 0.75, borderRadius: 'sm' }}>
        <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary', fontSize: '0.65rem' }}>
          HOUSE LORD
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35 }}>
          <Typography level="body-sm" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
            {lordName}
          </Typography>
          <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            (Lord of {signName})
          </Typography>
        </Box>

        {/* "Lord Me in H5 (3rd from own) · Virgo" — italic small description */}
        <Typography level="body-xs"
          sx={{ fontSize: '0.68rem', fontStyle: 'italic', color: 'primary.600', lineHeight: 1.4, mb: lordInterp ? 0.75 : 0 }}>
          {lordDescription}
        </Typography>

        {/* Lordship prediction text from house_lord_in_house_predictions */}
        {lordInterp && (
          <Typography level="body-xs"
            sx={{ fontSize: '0.67rem', color: 'text.secondary', lineHeight: 1.5, borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}>
            {lordInterp}
          </Typography>
        )}
      </Sheet>

      {/* ── Interpretation hint ── */}
      <Sheet data-audio-cue="chart-lord-hint" variant="soft" color="primary" sx={{ p: 0.75, borderRadius: 'sm', bgcolor: 'primary.softBg' }}>
        <Typography level="body-xs" sx={{ fontSize: '0.65rem', lineHeight: 1.4 }}>
          💡 Lord's position affects manifestation.
          {offset === 0 && ' In own house gives full control & direct results.'}
          {offset === 2 && ' 3rd brings effort & courage.'}
          {offset === 5 && ' 6th may bring challenges.'}
          {offset === 8 && ' 9th brings fortune & purpose.'}
          {offset === 10 && ' 11th brings gains.'}
        </Typography>
      </Sheet>
    </Box>
  );
}
